// DevTools: Add this file to your browser's ignore list for accurate console source attribution.
// Chrome/Edge: DevTools > Settings > Ignore List > Add pattern: SupportBundle
// Firefox: Debugger > right-click file > Ignore source
// Safari: Not supported — use the "source" field in captured log entries instead.
window.SupportBundle = (function () {

    const MAX_CONSOLE_LOGS = 1000;
    const SENSITIVE_STORAGE_KEY_PATTERN =
        /token|auth|secret|key|csrf|password|credential|session_id|^oidc\.user:/i;
    const consoleLogs = [];

    const originalConsole = {
        log: console.log,
        info: console.info,
        warn: console.warn,
        error: console.error
    };

    function stringifyArg(arg) {
        if (typeof arg === 'undefined') return 'undefined';
        if (arg === null) return 'null';

        try {
            if (typeof arg === 'object') {
                return JSON.stringify(arg);
            }
            return String(arg);
        } catch (e) {
            return `[Object that couldn't be stringified]`;
        }
    }

    function parseCallerFromStack(stack) {
        try {
            const lines = stack.split('\n').filter(line => line.trim());
            // Skip: "Error", addLogEntry frame, console.xxx wrapper frame
            const callerLine = lines[3] || lines[lines.length - 1] || '';

            // Chrome/Edge: "    at funcName (url:line:col)" or "    at url:line:col"
            let match = callerLine.match(/at\s+.*?\(?(https?:\/\/[^)]+|\/[^)]+):(\d+):(\d+)\)?/);
            if (match) {
                const file = match[1].split('/').pop();
                return `${file}:${match[2]}:${match[3]}`;
            }

            // Firefox/Safari: "funcName@url:line:col"
            match = callerLine.match(/@(https?:\/\/[^:]+|\/[^:]+):(\d+):(\d+)/);
            if (match) {
                const file = match[1].split('/').pop();
                return `${file}:${match[2]}:${match[3]}`;
            }

            return callerLine.trim();
        } catch (e) {
            return 'unknown';
        }
    }

    function addLogEntry(type, args) {
        const stack = new Error().stack || '';
        consoleLogs.push({
            type: type,
            timestamp: new Date().toISOString(),
            message: args.map(arg => stringifyArg(arg)),
            source: parseCallerFromStack(stack)
        });
        if (consoleLogs.length > MAX_CONSOLE_LOGS) {
            consoleLogs.splice(0, consoleLogs.length - MAX_CONSOLE_LOGS);
        }
    }

    console.log = (...args) => {
        addLogEntry('log', args);
        originalConsole.log.apply(console, args);
    };

    console.info = (...args) => {
        addLogEntry('info', args);
        originalConsole.info.apply(console, args);
    };

    console.warn = (...args) => {
        addLogEntry('warn', args);
        originalConsole.warn.apply(console, args);
    };

    console.error = (...args) => {
        addLogEntry('error', args);
        originalConsole.error.apply(console, args);
    };

    return {

        getConsoleLogs: function () {
            return JSON.stringify({
                logs: consoleLogs,
                totalCount: consoleLogs.length,
                timestamp: new Date().toISOString()
            }, null, 2);
        },

        getLocalStorage: function () {
            const storage = {};
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                try {
                    storage[key] = SENSITIVE_STORAGE_KEY_PATTERN.test(key)
                        ? '[REDACTED]'
                        : localStorage.getItem(key);
                } catch (e) {
                    storage[key] = `[Error reading value: ${e.message}]`;
                }
            }

            return JSON.stringify({
                storage: storage,
                itemCount: localStorage.length,
                timestamp: new Date().toISOString()
            }, null, 2);
        },

        getSessionStorage: function () {
            const storage = {};
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                try {
                    storage[key] = SENSITIVE_STORAGE_KEY_PATTERN.test(key)
                        ? '[REDACTED]'
                        : sessionStorage.getItem(key);
                } catch (e) {
                    storage[key] = `[Error reading value: ${e.message}]`;
                }
            }

            return JSON.stringify({
                storage: storage,
                itemCount: sessionStorage.length,
                timestamp: new Date().toISOString()
            }, null, 2);
        },

        getIndexedDbData: async function () {
            return new Promise(async (resolve) => {
                const databases = [];

                try {
                    let dbNames = [];
                    if ('databases' in indexedDB) {
                        const dbInfos = await indexedDB.databases();
                        dbNames = dbInfos.map(db => db.name);
                    } else {
                        dbNames = localStorage.getItem('known-dbs') ?
                            JSON.parse(localStorage.getItem('known-dbs')) : [];
                    }

                    if (!dbNames.length) {
                        resolve(JSON.stringify({
                            databases: [],
                            message: "No IndexedDB databases found",
                            timestamp: new Date().toISOString()
                        }, null, 2));
                        return;
                    }

                    let dbsProcessed = 0;

                    for (const dbName of dbNames) {
                        try {
                            const request = indexedDB.open(dbName);

                            request.onerror = () => {
                                databases.push({
                                    name: dbName,
                                    error: `Could not open database: ${request.error}`
                                });

                                checkCompletion();
                            };

                            request.onsuccess = (event) => {
                                const db = event.target.result;
                                const dbInfo = {
                                    name: db.name,
                                    version: db.version,
                                    objectStores: []
                                };

                                const objectStoreNames = Array.from(db.objectStoreNames);

                                for (const storeName of objectStoreNames) {
                                    try {
                                        const transaction = db.transaction(storeName, 'readonly');
                                        const store = transaction.objectStore(storeName);

                                        dbInfo.objectStores.push({
                                            name: storeName,
                                            keyPath: store.keyPath,
                                            autoIncrement: store.autoIncrement,
                                            indexNames: Array.from(store.indexNames)
                                        });
                                    } catch (e) {
                                        dbInfo.objectStores.push({
                                            name: storeName,
                                            error: `Error accessing store info: ${e.message}`
                                        });
                                    }
                                }

                                databases.push(dbInfo);
                                db.close();
                                checkCompletion();
                            };
                        } catch (e) {
                            databases.push({
                                name: dbName,
                                error: `Error opening database: ${e.message}`
                            });

                            checkCompletion();
                        }
                    }

                    function checkCompletion() {
                        dbsProcessed++;
                        if (dbsProcessed === dbNames.length) {
                            resolve(JSON.stringify({
                                databases: databases,
                                timestamp: new Date().toISOString()
                            }, null, 2));
                        }
                    }
                } catch (e) {
                    resolve(JSON.stringify({
                        error: `Error accessing IndexedDB: ${e.message}`,
                        timestamp: new Date().toISOString()
                    }, null, 2));
                }
            });
        },

        getSystemInfo: function () {
            const nav = window.navigator;
            const systemInfo = {
                userAgent: nav.userAgent,
                os: detectOS(),
                language: nav.language,
                cookieEnabled: nav.cookieEnabled,
                doNotTrack: nav.doNotTrack,
                hardwareConcurrency: nav.hardwareConcurrency || 'unknown',
                maxTouchPoints: nav.maxTouchPoints || 0,
                deviceMemory: nav.deviceMemory || 'unknown',

                screen: {
                    width: window.screen.width,
                    height: window.screen.height,
                    availWidth: window.screen.availWidth,
                    availHeight: window.screen.availHeight,
                    colorDepth: window.screen.colorDepth,
                    orientation: window.screen.orientation ?
                        window.screen.orientation.type : 'unknown'
                },

                window: {
                    innerWidth: window.innerWidth,
                    innerHeight: window.innerHeight,
                    scrollX: window.scrollX,
                    scrollY: window.scrollY,
                    devicePixelRatio: window.devicePixelRatio || 1
                },

                timing: window.performance ?
                    (window.performance.getEntriesByType &&
                    window.performance.getEntriesByType("navigation").length ?
                        window.performance.getEntriesByType("navigation")[0] :
                        window.performance.timing) : 'not available',

                connection: nav.connection ? {
                    effectiveType: nav.connection.effectiveType,
                    downlink: nav.connection.downlink,
                    rtt: nav.connection.rtt,
                    saveData: nav.connection.saveData
                } : 'not available',

                timestamp: new Date().toISOString()
            };

            return JSON.stringify(systemInfo, null, 2);

            function detectOS() {
                const userAgent = window.navigator.userAgent;
                const platform = {};

                platform.cookiesEnabled = !!navigator.cookieEnabled;
                platform.touchSupported = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
                platform.serviceWorkerSupported = 'serviceWorker' in navigator;

                if (/Windows NT/.test(userAgent)) {
                    platform.os = 'Windows';
                    const windowsVersion = userAgent.match(/Windows NT (\d+\.\d+)/);
                    if (windowsVersion) {
                        const versionMap = {
                            '10.0': '10/11',
                            '6.3': '8.1',
                            '6.2': '8',
                            '6.1': '7',
                            '6.0': 'Vista',
                            '5.2': 'XP 64-bit',
                            '5.1': 'XP'
                        };
                        platform.osVersion = versionMap[windowsVersion[1]] || windowsVersion[1];
                    }
                } else if (/Macintosh/.test(userAgent)) {
                    platform.os = 'macOS';
                    const macVersion = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
                    if (macVersion) {
                        platform.osVersion = macVersion[1].replace(/_/g, '.');
                    }
                } else if (/Android/.test(userAgent)) {
                    platform.os = 'Android';
                    const androidVersion = userAgent.match(/Android (\d+\.\d+)/);
                    if (androidVersion) {
                        platform.osVersion = androidVersion[1];
                    }
                } else if (/iPhone|iPad|iPod/.test(userAgent)) {
                    platform.os = 'iOS';
                    const iosVersion = userAgent.match(/OS (\d+[._]\d+[._]?\d*)/);
                    if (iosVersion) {
                        platform.osVersion = iosVersion[1].replace(/_/g, '.');
                    }
                } else if (/Linux/.test(userAgent)) {
                    platform.os = 'Linux';
                } else {
                    platform.os = 'Unknown';
                }

                if (/Firefox/.test(userAgent)) {
                    platform.browser = 'Firefox';
                } else if (/Edg/.test(userAgent)) {
                    platform.browser = 'Edge';
                } else if (/Chrome/.test(userAgent)) {
                    platform.browser = 'Chrome';
                } else if (/Safari/.test(userAgent)) {
                    platform.browser = 'Safari';
                } else if (/Trident|MSIE/.test(userAgent)) {
                    platform.browser = 'Internet Explorer';
                } else {
                    platform.browser = 'Unknown';
                }

                if ('getBattery' in navigator) {
                    platform.batteryApiSupported = true;
                }

                return platform;
            }
        },

        getNetworkInfo: function () {
            const networkInfo = {
                requests: [],
                timestamp: new Date().toISOString()
            };

            if (!window.performance || !window.performance.getEntries) {
                return JSON.stringify({
                    error: "Performance API not supported",
                    timestamp: new Date().toISOString()
                }, null, 2);
            }

            try {
                const entries = window.performance.getEntries();

                for (const entry of entries) {
                    if (entry.entryType === 'resource' || entry.entryType === 'navigation') {
                        const resourceInfo = {
                            name: entry.name,
                            entryType: entry.entryType,
                            startTime: entry.startTime,
                            duration: entry.duration,
                            initiatorType: entry.initiatorType
                        };

                        if (entry instanceof PerformanceResourceTiming) {
                            resourceInfo.timing = {
                                redirectTime: entry.redirectEnd - entry.redirectStart,
                                dnsTime: entry.domainLookupEnd - entry.domainLookupStart,
                                connectTime: entry.connectEnd - entry.connectStart,
                                requestTime: entry.responseStart - entry.requestStart,
                                responseTime: entry.responseEnd - entry.responseStart
                            };

                            if (entry.serverTiming) {
                                resourceInfo.serverTiming = entry.serverTiming;
                            }
                        }

                        networkInfo.requests.push(resourceInfo);
                    }
                }

                return JSON.stringify(networkInfo, null, 2);
            } catch (e) {
                return JSON.stringify({
                    error: `Error getting network info: ${e.message}`,
                    timestamp: new Date().toISOString()
                }, null, 2);
            }
        },

        getAppState: function () {
            return JSON.stringify({
                currentUrl: window.location.href,
                pageTitle: document.title,
                timestamp: new Date().toISOString()
            }, null, 2);
        },

        downloadFile: function (fileName, base64Content) {
            try {
                const byteCharacters = atob(base64Content);
                const byteArrays = [];

                const sliceSize = 512;
                for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                    const slice = byteCharacters.slice(offset, offset + sliceSize);

                    const byteNumbers = new Array(slice.length);
                    for (let i = 0; i < slice.length; i++) {
                        byteNumbers[i] = slice.charCodeAt(i);
                    }

                    const byteArray = new Uint8Array(byteNumbers);
                    byteArrays.push(byteArray);
                }

                const blob = new Blob(byteArrays, {type: 'application/zip'});
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();

                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }, 100);

                return true;
            } catch (error) {
                console.error("Error downloading file:", error);
                return false;
            }
        }
    };
})();
